import React, { useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Button, capitalize, Chip, IconButton, Stack, TextField, Typography } from "@mui/material";
import { SelectAutocomplete } from "components/forms";
import { useTaskDealsSelect } from "store/selectors/task-manage-selectors";
import { DealBase } from "store/types";

import { EmailInboxFilter, FilterKind } from "../EmailInbox";

interface Props {
  kind: FilterKind;
  onCancel: () => void;
  onSubmit: (value: { isSubmitted: boolean; data: DealBase[] | string | null }) => void;
  filter: EmailInboxFilter;
}

export const FilterContent: React.FC<Props> = ({ kind, onSubmit, filter, onCancel }) => {
  const deals = useTaskDealsSelect();

  const [localFilters, setLocalFilters] = useState<EmailInboxFilter>({
    deal: filter.deal,
    association: filter.association,
    attachment: filter.attachment
  });

  const options = useMemo(() => {
    switch (kind) {
      case FilterKind.DEAL:
        return deals;
      case FilterKind.ASSOCIATION:
        return ["Suggested only", "Associated only"];
      case FilterKind.ATTACHMENT:
        return ["With attachment", "Without attachment"];
    }
  }, [kind]);

  return (
    <Stack
      sx={{
        outline: "none",
        bgcolor: "white",
        width: "410px",
        p: "32px",
        gap: "20px",
        borderRadius: "8px",
        borderShadow: "sm",
        maxHeight: "500px",
        overflow: "hidden"
      }}
    >
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", borderBottom: "1px solid #DBE8F5" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 400, lineHeight: "24px" }}> {capitalize(kind)}</Typography>
        <IconButton onClick={() => onSubmit({ isSubmitted: false, data: null })}>
          <CloseIcon />
        </IconButton>
      </Stack>
      {kind === FilterKind.DEAL && (
        <SelectAutocomplete
          fullWidth
          options={deals}
          value={localFilters.deal}
          multiple
          selectAll
          setValue={(option) => setLocalFilters({ ...localFilters, deal: option || [] })}
          renderInput={(params) => <TextField {...params} label="Deal" />}
          disableCloseOnSelect
          getOptionLabel={(option) => option.title}
          compareParam={(option) => option.title}
          renderTags={(tagValue, getTagProps) => {
            return (
              <div style={{ maxHeight: "120px", overflowY: "auto", overflowX: "hidden" }}>
                {tagValue.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={index} label={option.title} />
                ))}
              </div>
            );
          }}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
            <li {...props} key={option.id}>
              {option.title}
            </li>
          )}
        />
      )}
      {(kind === FilterKind.ASSOCIATION || kind === FilterKind.ATTACHMENT) && (
        <Autocomplete
          fullWidth
          value={kind === FilterKind.ASSOCIATION ? localFilters.association : localFilters.attachment}
          options={options as string[]}
          renderInput={(params) => <TextField {...params} />}
          onChange={(_, option) =>
            setLocalFilters({
              ...localFilters,
              [kind === FilterKind.ASSOCIATION ? "association" : "attachment"]: option || null
            })
          }
        />
      )}

      <Stack sx={{ flexDirection: "row", justifyContent: "flex-end", gap: "8px" }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onSubmit({
              isSubmitted: true,
              data: localFilters[kind]
            });
          }}
        >
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};
